import React, { useRef } from "react";
import { ARABIC_KEY_MAP } from "./arabicKeyMap";

interface ArabicTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const isPrintableKey = (e: React.KeyboardEvent<HTMLInputElement>) =>
  e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

const ArabicTextField: React.FC<ArabicTextFieldProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  className = "",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isPrintableKey(e)) return;

    const mapped = ARABIC_KEY_MAP[e.key];
    if (!mapped) return;

    e.preventDefault();

    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;

    const nextValue =
      value.slice(0, start) + mapped + value.slice(end);

    onChange(nextValue);

    // Restore cursor safely
    requestAnimationFrame(() => {
      input.setSelectionRange(start + mapped.length, start + mapped.length);
    });
  };

  return (
    <input
      ref={inputRef}
      dir="rtl"
      lang="ar"
      type="text"
      value={value}
      disabled={disabled}
      placeholder={placeholder || "اكتب بالعربية"}
      onKeyDown={handleKeyDown}
      onChange={(e) => onChange(e.target.value)} // allows paste / mobile
      className={`
        w-full rounded-lg border px-3 py-2
        text-right leading-relaxed
        focus:ring-2 focus:ring-blue-500 outline-none
        font-arabic
        ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}
        ${className}
      `}
      style={{
        fontFamily:
          "'Amiri','Scheherazade New','Cairo','Noto Naskh Arabic',serif",
      }}
    />
  );
};

export default ArabicTextField;